import React from 'react';
import Icon from '../../../components/AppIcon';

const SectionRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', minWidth: '140px' }}>{label}</span>
    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', textAlign: 'right', maxWidth: '200px', wordBreak: 'break-word' }}>
      {value || <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>}
    </span>
  </div>
);

const ReviewSection = ({ title, icon, children }) => (
  <div
    className="rounded-xl p-4"
    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon name={icon} size={15} color="#14B8A6" />
      <span className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>{title}</span>
    </div>
    {children}
  </div>
);

const EMPLOYMENT_LABELS = {
  employed: 'Employed',
  self_employed: 'Self-Employed',
  creator_influencer: 'Creator / Influencer',
  student: 'Student',
  other: 'Other',
};

const INCOME_LABELS = {
  under_15k: 'Under $15,000',
  '15k_30k': '$15,000 – $30,000',
  '30k_60k': '$30,000 – $60,000',
  '60k_100k': '$60,000 – $100,000',
  over_100k: 'Over $100,000',
};

const DOC_LABELS = {
  passport: 'Passport',
  national_id: 'National ID',
  drivers_license: "Driver\'s License",
};

const ReviewSubmitStep = ({ personalData, kycData, socialData, scScore, termsAccepted, consentAccepted, onTermsChange, onConsentChange, submitting, submitError }) => {
  const addr = personalData?.address || {};
  const addressStr = [addr?.street, addr?.city, addr?.state, addr?.postcode, addr?.country]?.filter(Boolean)?.join(', ');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Review Your Application
        </h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Please review all details carefully before submitting.
        </p>
      </div>
      {/* Personal Info */}
      <ReviewSection title="Personal Information" icon="User">
        <SectionRow label="Full Name" value={personalData?.fullName} />
        <SectionRow label="Date of Birth" value={personalData?.dob} />
        <SectionRow label="Nationality" value={personalData?.nationality} />
        <SectionRow label="Phone" value={`${personalData?.phoneCode || ''} ${personalData?.phone || ''}`} />
        <SectionRow label="Address" value={addressStr} />
        <SectionRow label="Employment" value={EMPLOYMENT_LABELS?.[personalData?.employmentStatus]} />
        <SectionRow label="Annual Income" value={INCOME_LABELS?.[personalData?.incomeRange]} />
      </ReviewSection>
      {/* KYC */}
      <ReviewSection title="Identity Verification" icon="ShieldCheck">
        <SectionRow label="Document Type" value={DOC_LABELS?.[kycData?.documentType]} />
        <SectionRow label="Document Expiry" value={kycData?.documentExpiry} />
        <SectionRow
          label="Front Document"
          value={kycData?.frontUrl ? '✓ Uploaded' : 'Not uploaded'}
        />
        {kycData?.backUrl && <SectionRow label="Back Document" value="✓ Uploaded" />}
        <SectionRow
          label="Selfie"
          value={kycData?.selfieUrl ? '✓ Uploaded' : 'Not uploaded'}
        />
      </ReviewSection>
      {/* Social */}
      <ReviewSection title="Social & Creator" icon="Star">
        <SectionRow label="Social Handle" value={`@${socialData?.socialHandle || ''}`} />
        <SectionRow label="Primary Platform" value={socialData?.platform} />
        <SectionRow label="Audience Size" value={socialData?.audienceSize} />
        <SectionRow label="Monthly Earnings" value={socialData?.monthlyEarnings} />
        <SectionRow label="SocialCents Score" value={String(scScore || 0)} />
      </ReviewSection>
      {/* Consent */}
      <div className="space-y-3">
        {[{
          id: 'terms',
          checked: termsAccepted,
          onChange: onTermsChange,
          label: 'I have read and agree to the SocialCents Card Terms & Conditions and Privacy Policy.',
        }, {
          id: 'consent',
          checked: consentAccepted,
          onChange: onConsentChange,
          label: 'I consent to SocialCents performing identity verification and a soft credit check as part of this application.',
        }]?.map((item) => (
          <label
            key={item?.id}
            className="flex items-start gap-3 cursor-pointer p-3 rounded-xl transition-all"
            style={{ background: item?.checked ? 'rgba(20,184,166,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${item?.checked ? 'rgba(20,184,166,0.25)' : 'rgba(255,255,255,0.07)'}` }}
          >
            <div
              className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
              style={{
                background: item?.checked ? 'linear-gradient(135deg,#0d9488,#1d4ed8)' : 'rgba(255,255,255,0.05)',
                border: item?.checked ? 'none' : '1.5px solid rgba(255,255,255,0.2)',
              }}
            >
              {item?.checked && <Icon name="Check" size={12} color="#fff" />}
            </div>
            <input type="checkbox" checked={item?.checked} onChange={(e) => item?.onChange(e?.target?.checked)} className="hidden" />
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: '1.5' }}>{item?.label}</span>
          </label>
        ))}
      </div>
      {submitError && (
        <div
          className="flex items-start gap-3 p-4 rounded-xl"
          style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}
        >
          <Icon name="AlertCircle" size={16} color="#f87171" className="flex-shrink-0 mt-0.5" />
          <p style={{ fontSize: '13px', color: '#f87171' }}>{submitError}</p>
        </div>
      )}
      {submitting && (
        <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(20,184,166,0.06)', border: '1px solid rgba(20,184,166,0.2)' }}>
          <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#14B8A6', borderTopColor: 'transparent' }} />
          <p style={{ fontSize: '13px', color: '#14B8A6' }}>Submitting your application…</p>
        </div>
      )}
    </div>
  );
};

export default ReviewSubmitStep;
